<template>
  <div class="h-full flex flex-col overflow-hidden bg-gray-50">
    <div class="p-6 pb-0 flex-none  mx-auto w-full">
      <BreadCrumbs :navigation="navigation" />
    </div>

    <!-- Header / filtri -->
    <div class="flex-none px-6 pt-4 pb-0 mx-auto w-full">
      <div class="bg-white border border-gray-200 shadow-sm rounded-xl p-5">
        <div class="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <div class="flex items-center gap-2">
              <span class="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-blue-50 text-blue-600">
                <v-icon name="bi-calendar-event"></v-icon>
              </span>
              <div>
                <h1 class="text-xl font-semibold text-gray-900">Calendario attività</h1>
                <p class="text-sm text-gray-500 mt-0.5">Visualizza attività, slot pianificati e scadenze.</p>
              </div>
            </div>
          </div>

          <div class="flex items-center gap-2">
            <button
              type="button"
              @click="openCalendarFeedModal"
              class="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <v-icon name="bi-calendar2-plus"></v-icon>
              Collega calendario
            </button>
            <button
              v-if="hasActiveFilters"
              type="button"
              @click="resetFilters"
              class="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <v-icon name="bi-arrow-counterclockwise"></v-icon>
              Reset filtri
            </button>
          </div>
        </div>

        <div class="mt-5 grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-3 items-end">
          <div>
            <label class="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Priorità</label>
            <select v-model="filterPriority" @change="refreshCalendar" class="calendar-select">
              <option value="">Tutte</option>
              <option value="bassa">Bassa</option>
              <option value="media">Media</option>
              <option value="alta">Alta</option>
              <option value="critica">Critica</option>
            </select>
          </div>

          <div>
            <label class="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Stato</label>
            <select v-model="filterStatus" @change="refreshCalendar" class="calendar-select">
              <option value="">Tutti</option>
              <option value="nuova">Nuova</option>
              <option value="assegnata">Assegnata</option>
              <option value="schedulata">Schedulata</option>
              <option value="in_lavorazione">In lavorazione</option>
              <option value="completata">Completata</option>
            </select>
          </div>

          <div>
            <label class="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Assegnatario</label>
            <select v-model="filterUser" @change="refreshCalendar" class="calendar-select">
              <option value="">Tutti</option>
              <option v-for="user in users" :key="user.id" :value="user.name">{{ user.name }}</option>
            </select>
          </div>

          <div class="xl:col-span-1">
            <p class="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Legenda</p>
            <div v-if="$page.props.isAdminOrSuperAdmin || isManager" class="flex flex-wrap gap-x-3 gap-y-1.5 text-xs text-gray-600 max-h-20 overflow-y-auto">
              <span
                v-for="user in (users || [])"
                :key="user.id"
                class="legend-item"
              >
                <span
                  class="legend-dot"
                  :style="{ backgroundColor: userColorMap[user.id] }"
                ></span>
                {{ user.name }}
              </span>
              <span class="legend-item">
                <span class="legend-dot" style="background-color: #9ca3af;"></span>
                Completata
              </span>
              <span class="legend-item">
                  <span
                    class="legend-dot"
                    style="background: repeating-linear-gradient(45deg, #fed7aa 0, #fed7aa 4px, #fb923c 4px, #fb923c 8px); border: 1px solid #ea580c;"
                  ></span>
                  Non assegnata operativa
                </span>
            </div>
            <div v-else class="flex flex-wrap gap-x-3 gap-y-2 text-xs text-gray-500">
              <span class="legend-item"><span class="legend-dot bg-green-500"></span>Bassa</span>
              <span class="legend-item"><span class="legend-dot bg-blue-500"></span>Media</span>
              <span class="legend-item"><span class="legend-dot bg-orange-500"></span>Alta</span>
              <span class="legend-item"><span class="legend-dot bg-red-500"></span>Critica</span>
              <span class="legend-item"><span class="legend-dot bg-gray-400"></span>Completata</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Calendario + Sidebar Promemoria -->
    <div class="flex-1 min-h-0 px-6 pb-6 pt-4 mx-auto w-full">
      <div class="flex flex-col lg:flex-row gap-4" style="height: calc(100vh - 12.5rem);">

        <!-- Sidebar Promemoria Coda Reparto -->
        <aside class="lg:w-72 xl:w-80 flex-shrink-0">
          <div class="bg-white border border-slate-200 rounded-xl shadow-sm flex flex-col overflow-hidden sticky top-0" style="max-height: calc(100vh - 12rem);">
            <div class="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-amber-50 to-white flex-shrink-0">
              <div class="flex items-center gap-2">
                <span class="w-7 h-7 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center">
                  <v-icon name="hi-solid-bell" class="w-4 h-4" />
                </span>
                <span class="text-sm font-semibold text-slate-800">Promemoria in coda</span>
              </div>
              <span
                class="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-bold"
                :class="localQueue.length > 0 ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-400'"
              >
                {{ localQueue.length }}
              </span>
            </div>

            <div v-if="localQueue.length > 0" class="px-3 py-2 bg-blue-50 border-b border-blue-100 flex-shrink-0">
              <p class="text-xs text-blue-600 flex items-center gap-1.5">
                <v-icon name="hi-solid-information-circle" class="w-3.5 h-3.5 flex-shrink-0" />
                Trascina sul calendario o usa il tasto per prendere in carico
              </p>
            </div>

            <div id="external-reminders-container" class="flex-1 overflow-y-auto p-3 space-y-2">
              <div
                v-if="localQueue.length === 0"
                class="flex flex-col items-center justify-center py-10 text-slate-300"
              >
                <v-icon name="hi-solid-check-circle" class="w-10 h-10 mb-2" />
                <p class="text-xs font-medium text-center text-slate-400">Nessun promemoria da prendere in carico</p>
              </div>

              <div
                v-for="reminder in localQueue"
                :key="reminder.id"
                class="reminder-drag-item cursor-grab active:cursor-grabbing border border-amber-200 rounded-xl p-3 shadow-sm select-none hover:shadow-md transition-shadow"
                style="background: linear-gradient(135deg, #fef9c3 0%, #fef3c7 100%);"
                :data-reminder-id="reminder.id"
                :data-title="reminder.title"
              >
                <div class="flex items-start justify-between gap-1.5 mb-1">
                  <p class="text-xs font-semibold text-slate-800 leading-snug line-clamp-2 flex-1">{{ reminder.title }}</p>
                  <v-icon name="hi-arrows-expand" class="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
                </div>
                <p v-if="reminder.description" class="text-xs text-slate-400 line-clamp-1 mb-1.5">{{ reminder.description }}</p>
                <div class="flex items-center gap-2 flex-wrap mb-2">
                  <span v-if="reminder.department" class="text-xs text-indigo-600 font-medium">{{ reminder.department.name }}</span>
                  <span class="text-xs text-slate-400 ml-auto">{{ reminder.creator?.name ?? '—' }}</span>
                </div>
                <button
                  type="button"
                  @click.stop="apriPresaInCaricoModal(reminder)"
                  class="w-full py-1 rounded-lg text-xs font-semibold bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors border border-emerald-200"
                >
                  Prendi in carico
                </button>
              </div>
            </div>
          </div>
        </aside>

        <!-- Area calendario -->
        <div class="flex-1 min-w-0 min-h-0 flex flex-col gap-3">
          <Loader :visible="isLoading" text="Caricamento..." />

          <transition name="action-panel-fade">
            <div
              v-if="calendarConflictError"
              class="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            >
              <svg class="h-4 w-4 flex-none" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
              <span class="flex-1">{{ calendarConflictError }}</span>
              <button type="button" @click="calendarConflictError = null" class="text-red-400 hover:text-red-600">
                <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </transition>

          <div class="flex-1 min-h-0 flex flex-col bg-white border border-gray-200 shadow-sm rounded-xl p-4 overflow-hidden">
            <FullCalendar ref="fullCalendar" :options="calendarOptions" class="activity-calendar flex-1 min-h-0" />
          </div>
        </div>

      </div>
    </div>

    <!-- Drawer dettaglio evento -->
    <transition name="action-panel-fade">
      <div
        v-if="eventDrawer.show"
        class="fixed inset-0 bg-gray-900 bg-opacity-40 z-50"
        @click.self="closeEventDrawer"
      >
        <transition name="action-panel-slide" appear>
          <aside
            v-if="eventDrawer.show"
            class="absolute right-0 top-20 h-[calc(100%-5rem)] w-full max-w-2xl bg-white shadow-2xl flex flex-col rounded-l-xl"
            @click.stop
          >
            <!-- Header drawer -->
            <div class="flex-none px-6 py-5 border-b border-gray-200 bg-white rounded-tl-xl">
              <div class="flex items-start justify-between gap-4">
                <div class="min-w-0 flex-1">
                  
                  <h3 class="text-lg font-semibold text-gray-900 truncate">
                    {{ currentCalendarEvent ? currentCalendarEvent.title : '' }}
                  </h3>
                  <div class="flex flex-wrap items-center gap-2 mt-2">
                    <span
                      :class="prioritySoftBadgeClass(currentEventProps.priority)"
                      class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                    >
                      {{ priorityLabel(currentEventProps.priority) }}
                    </span>
                    <span
                      :class="statusBadgeClass(currentEventProps.status)"
                      class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                    >
                      {{ statusLabel(currentEventProps.status) }}
                    </span>
                  </div>
                </div>
                <div class="flex items-center gap-1">
                  
                  <button
                    type="button"
                    @click="closeEventDrawer"
                    class="flex-none rounded-full p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    title="Chiudi"
                  >
                    <i class="bi bi-x-lg"></i>
                  </button>
                  <button
                    v-if="canManageActivities"
                    type="button"
                    @click="deleteActivityFromDrawer"
                    :disabled="isDeletingActivity"
                    class="flex-none rounded-full p-2 text-red-600 bg-red-100 hover:text-red-700 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Elimina attività"
                  >
                    <svg
                      class="w-4 h-4"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </button>
                  <button
                      v-if="canManageActivities"
                      type="button"
                      @click="editActivityFromDrawer"
                      class="flex-none rounded-full p-2 text-amber-600 bg-amber-100 hover:text-amber-700 hover:bg-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-colors"
                      title="Modifica attività"
                    >
                      <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" />
                        <path fill-rule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clip-rule="evenodd"/>
                      </svg>
                  </button>

                  <Link
                    v-if="currentEventProps.order_id && currentEventProps.activity_series_id == null"
                    :href="route('modules.attivita.gestione-ordine', currentEventProps.order_id)"
                    class="inline-flex items-center justify-center gap-2 rounded-full px-3 py-2 text-sm font-medium text-blue-600 bg-blue-100 hover:text-blue-700 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                  >
                    Apri ordine
                  </Link>

                  <Link
                    v-else-if="currentEventProps.order_id && currentEventProps.activity_series_id != null"
                    :href="route('modules.attivita.gestione-contratto', currentEventProps.order_id)"
                    class="inline-flex items-center justify-center gap-2 rounded-full px-3 py-2 text-sm font-medium text-blue-600 bg-blue-100 hover:text-blue-700 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                  >
                    Vai alla lista
                  </Link>

                  <Link
                    v-else
                    :href="route('modules.attivita.lista-attivita')"
                    class="inline-flex items-center justify-center gap-2 rounded-full px-3 py-2 text-sm font-medium text-blue-600 bg-blue-100 hover:text-blue-700 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                  >
                    Vai alla lista
                  </Link>
                </div>
              </div>
            </div>

            <!-- Body scrollabile -->
            <div class="flex-1 overflow-y-auto px-6 py-6 bg-gray-50 space-y-5">
              <!-- Sezione 1: Informazioni principali -->
              <div>
                <p class="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Informazioni</p>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div class="info-card sm:col-span-2">
                    <span class="info-label">Cliente</span>
                    <span class="info-value">{{ currentEventProps.company_name || '—' }}</span>
                  </div>
                  <div v-if="currentEventProps.description" class="info-card sm:col-span-2">
                    <span class="info-label">Descrizione</span>
                    <span class="info-value whitespace-pre-line">{{ currentEventProps.description }}</span>
                  </div>
                  <div class="info-card">
                    <span class="info-label">Priorità</span>
                    <span class="info-value">
                      <span :class="prioritySoftBadgeClass(currentEventProps.priority)" class="inline-flex px-2 py-0.5 rounded-full text-xs font-medium">
                        {{ priorityLabel(currentEventProps.priority) }}
                      </span>
                    </span>
                  </div>
                  <div class="info-card">
                    <span class="info-label">Stato</span>
                    <span class="info-value">
                      <span :class="statusBadgeClass(currentEventProps.status)" class="inline-flex px-2 py-0.5 rounded-full text-xs font-medium">
                        {{ statusLabel(currentEventProps.status) }}
                      </span>
                    </span>
                  </div>
                  <div class="info-card sm:col-span-2">
                    <span class="info-label">Assegnatari</span>
                    <span class="info-value">{{ currentEventProps.owners || '—' }}</span>
                  </div>
                  <div v-if="currentEventProps.order_id" class="info-card sm:col-span-2">
                    <span class="info-label">Ordine</span>
                    <span class="info-value">{{ currentEventProps.order_id }}</span>
                  </div>
                </div>
              </div>

              
              

              <!-- Sezione 2b: Ricezione merce -->
              <div v-if="currentEventProps.is_goods_receipt">
                <p class="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Ricezione merce</p>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div class="info-card">
                    <span class="info-label">Data stimata arrivo</span>
                    <span class="info-value">{{ currentEventProps.goods_estimated_arrival_at ? formatDate(new Date(currentEventProps.goods_estimated_arrival_at)) : '—' }}</span>
                  </div>
                  <div class="info-card">
                    <span class="info-label">Stato ricezione</span>
                    <span class="info-value">
                      <span v-if="currentEventProps.goods_receipt_status === 'waiting'" class="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                        In attesa merce
                      </span>
                      <span v-else-if="currentEventProps.goods_receipt_status === 'received'" class="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        Merce ricevuta
                      </span>
                      <span v-else>{{ currentEventProps.goods_receipt_status || '—' }}</span>
                    </span>
                  </div>
                  <div v-if="currentEventProps.goods_received_at" class="info-card sm:col-span-2">
                    <span class="info-label">Ricevuta il</span>
                    <span class="info-value">{{ formatDate(new Date(currentEventProps.goods_received_at)) }}</span>
                  </div>
                </div>
                <div v-if="currentEventProps.goods_receipt_status === 'waiting'" class="mt-3">
                  <button
                    type="button"
                    @click="markGoodsReceived"
                    :disabled="timerLoading"
                    class="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    <i class="bi bi-box-seam"></i>
                    Merce ricevuta
                  </button>
                </div>
              </div>

              <!-- Sezione 3: Lavorazione / Timer -->
              <div v-if="!currentEventProps.is_goods_receipt || currentEventProps.goods_receipt_status !== 'waiting'">
                <p class="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Lavorazione</p>

                <div v-if="timerLoading" class="flex items-center gap-2 text-sm text-gray-500">
                  <i class="bi bi-hourglass-split"></i>
                  Operazione in corso...
                </div>

                <template v-else>
                  <div v-if="hasRunningTimerOnOtherActivity()" class="rounded-lg border border-orange-200 bg-orange-50 p-4 text-sm text-orange-800">
                    <div class="flex items-start gap-2">
                      <i class="bi bi-exclamation-triangle mt-0.5 flex-none"></i>
                      <p>Hai già una lavorazione attiva: <strong>{{ runningTimer.activity_title }}</strong>. Fermala prima di iniziarne un'altra.</p>
                    </div>
                  </div>

                  <div v-else-if="isTimerRunningForCurrentActivity()" class="space-y-4">
                    <div class="rounded-lg border border-green-200 bg-green-50 p-4">
                      <div class="flex items-center gap-2 mb-1">
                        <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-500 text-white">
                          <i class="bi bi-play-fill"></i>
                          Timer attivo
                        </span>
                      </div>
                      <p class="text-sm text-green-800 mt-1">
                        Avviato alle <strong>{{ formatTimerTime(runningTimer.started_at) }}</strong>
                      </p>
                    </div>

                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-1">Note (opzionale)</label>
                      <textarea
                        v-model="timerDescription"
                        rows="2"
                        class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Descrizione del lavoro svolto..."
                      ></textarea>
                    </div>

                    <div class="flex flex-wrap gap-3">
                      <button
                        type="button"
                        @click="stopTimerFromDrawer"
                        :disabled="timerLoading"
                        class="inline-flex items-center justify-center gap-2 rounded-full px-3 py-2 text-sm font-medium text-yellow-600 bg-yellow-100 hover:text-yellow-700 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-yellow-500 transition-colors"
                      >
                        Pausa
                      </button>
                      <button
                        type="button"
                        @click="stopAndCompleteFromDrawer"
                        :disabled="timerLoading"
                        class="inline-flex items-center justify-center gap-2 rounded-full px-3 py-2 text-sm font-medium bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
                      >
                        
                        Attività completata
                      </button>
                    </div>
                  </div>

                  <div v-else class="space-y-3">
                    <p class="text-sm text-gray-600">Avvia il timer per registrare le ore lavorate su questa attività.</p>
                    <button
                      type="button"
                      @click="startTimerFromDrawer"
                      :disabled="timerLoading"
                      class="inline-flex items-center justify-center gap-2 rounded-full px-3 py-2 text-sm font-medium text-blue-600 bg-blue-100 hover:text-blue-700 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                    >
                      Avvia lavorazione
                    </button>
                  </div>
                </template>

                <div v-if="eventDrawer.error" class="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  <div class="flex items-start gap-2">
                    <i class="bi bi-exclamation-triangle mt-0.5 flex-none"></i>
                    <p>{{ eventDrawer.error }}</p>
                  </div>
                </div>
              </div>
            </div>

            
          </aside>
        </transition>
      </div>
    </transition>

    <!-- Google Calendar feed -->
    <transition name="modal-fade">
      <div
        v-if="calendarFeedModal.show"
        class="fixed inset-0 bg-gray-900 bg-opacity-40 flex items-center justify-center z-50 p-4"
        @click.self="calendarFeedModal.show = false"
      >
        <div class="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
          <div class="px-6 py-5 border-b border-gray-200 bg-white">
            <div class="flex items-start justify-between gap-4">
              <div>
                <span class="text-xs font-semibold uppercase tracking-wide text-gray-500">Integrazione</span>
                <h3 class="text-xl font-semibold text-gray-900 mt-1">Collega calendario</h3>
              </div>
              <button
                type="button"
                @click="calendarFeedModal.show = false"
                class="rounded-full p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <i class="bi bi-x-lg"></i>
              </button>
            </div>
          </div>

          <div class="px-6 py-5 space-y-4">
            <div v-if="calendarFeedModal.loading" class="text-sm text-gray-500 text-center py-4">
              Caricamento...
            </div>

            <template v-else-if="calendarFeed">
              <div v-if="calendarFeed.active">
                <label class="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Link calendario</label>
                <div class="flex gap-2">
                  <input
                    type="text"
                    readonly
                    :value="calendarFeed.url"
                    class="flex-1 text-sm border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-gray-700 select-all"
                    @click="$event.target.select()"
                  />
                  <button
                    type="button"
                    @click="copyCalendarFeedUrl"
                    class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    <i class="bi bi-clipboard"></i>
                    Copia
                  </button>
                </div>
              </div>

              <div v-else class="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                <div class="flex gap-2">
                  <i class="bi bi-pause-circle mt-0.5"></i>
                  <span>Feed disattivato. Il link non è più attivo.</span>
                </div>
              </div>

              <div v-if="calendarFeedModal.feedback" :class="calendarFeedModal.feedback.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'" class="rounded-lg border p-3 text-sm">
                {{ calendarFeedModal.feedback.message }}
              </div>

              <div class="rounded-lg border border-blue-100 bg-blue-50 p-4 text-sm text-blue-800 space-y-1.5">
                <p><strong>Come collegare il calendario:</strong></p>
                <p><strong>Google Calendar:</strong> Altri calendari → Da URL → incolla il link.</p>
                <p><strong>Outlook:</strong> Aggiungi calendario → Da Internet → incolla il link.</p>
                <p><strong>Apple Calendar:</strong> File → Nuovo abbonamento calendario → incolla il link.</p>
                <p class="text-blue-600 text-xs mt-2">Gli aggiornamenti potrebbero non essere immediati. Il gestionale resta la fonte ufficiale.</p>
              </div>

              <div class="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-500">
                <i class="bi bi-shield-lock mr-1"></i>
                Chiunque abbia questo link può visualizzare gli eventi esportati.
              </div>

              <div class="flex flex-wrap gap-2 pt-1">
                <button
                  v-if="calendarFeed.active"
                  type="button"
                  @click="disableCalendarFeed"
                  class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border border-amber-300 text-amber-700 hover:bg-amber-50 transition-colors"
                >
                  <i class="bi bi-pause-circle"></i>
                  Disattiva link
                </button>
                <button
                  type="button"
                  @click="regenerateCalendarFeed"
                  class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <i class="bi bi-arrow-repeat"></i>
                  {{ calendarFeed.active ? 'Rigenera link' : 'Genera nuovo link' }}
                </button>
              </div>
            </template>
          </div>
        </div>
      </div>
    </transition>

    <!-- Pianificazione slot -->
    <transition name="modal-fade">
      <div
        v-if="splitDialog.show"
        class="fixed inset-0 bg-gray-900 bg-opacity-40 flex items-center justify-center z-50 p-4"
        @click.self="splitDialog.show = false"
      >
        <div class="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden">
          <div class="px-6 py-5 border-b border-gray-200 bg-white">
            <div class="flex items-start justify-between gap-4">
              <div>
                <span class="text-xs font-semibold uppercase tracking-wide text-gray-500">Scheduling</span>
                <h3 class="text-xl font-semibold text-gray-900 mt-1">Pianifica slot</h3>
                <p class="text-sm text-gray-500 mt-1 truncate">{{ splitDialog.activityTitle }}</p>
              </div>

              <button
                type="button"
                @click="splitDialog.show = false"
                class="rounded-full p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <i class="bi bi-x-lg"></i>
              </button>
            </div>
          </div>

          <div class="px-6 py-5 space-y-5 bg-gray-50">
            <div class="rounded-lg border border-blue-100 bg-blue-50 p-4 text-sm text-blue-800">
              <div class="flex gap-3">
                <i class="bi bi-info-circle mt-0.5"></i>
                <p>
                  Il backend calcolerà gli slot rispettando orario lavorativo, pausa pranzo e regole di allocazione.
                  Il frontend invia solo partenza, durata e modalità.
                </p>
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Data partenza</label>
                <input
                  v-model="splitDialog.startDate"
                  type="date"
                  class="form-input-soft"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Ora partenza</label>
                <input
                  v-model="splitDialog.startTime"
                  type="time"
                  class="form-input-soft"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Durata totale, ore</label>
                <input
                  v-model.number="splitDialog.durationHours"
                  type="number"
                  min="0.5"
                  step="0.5"
                  class="form-input-soft"
                  placeholder="es. 6"
                />
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Modalità allocazione</label>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button
                  type="button"
                  @click="splitDialog.allocationMode = 'segmented'"
                  :class="allocationModeCardClass('segmented')"
                >
                  <span class="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-white border border-blue-100 text-blue-600">
                    <i class="bi bi-layout-split"></i>
                  </span>
                  <span class="block text-left">
                    <span class="block font-semibold text-gray-900">Segmentata</span>
                    <span class="block text-sm text-gray-500 mt-1">Distribuisce la durata su più slot lavorativi disponibili.</span>
                  </span>
                </button>

                <button
                  type="button"
                  @click="splitDialog.allocationMode = 'monolithic'"
                  :class="allocationModeCardClass('monolithic')"
                >
                  <span :class="splitDialog.allocationMode === 'monolithic' ? 'border-indigo-200 text-indigo-600' : 'border-indigo-100 text-indigo-400'" class="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-white border">
                    <i class="bi bi-square"></i>
                  </span>
                  <span class="block text-left">
                    <span class="block font-semibold text-gray-900">Monolitica</span>
                    <span class="block text-sm text-gray-500 mt-1">Richiede un unico blocco continuo. Se non disponibile, il salvataggio fallisce.</span>
                  </span>
                </button>
              </div>
            </div>

            <div v-if="splitDialog.error" class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              <div class="flex gap-3">
                <i class="bi bi-exclamation-triangle mt-0.5"></i>
                <p>{{ splitDialog.error }}</p>
              </div>
            </div>
          </div>

          <div class="px-6 py-4 border-t border-gray-200 bg-white">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2">
              <button
                type="button"
                @click="splitDialog.show = false"
                class="px-4 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Annulla
              </button>

              <button
                type="button"
                @click="saveSplit"
                :disabled="!canSavePlanning || isSaving"
                class="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <i v-if="!isSaving" class="bi bi-check2-circle"></i>
                <span v-if="isSaving">Pianificazione...</span>
                <span v-else>Pianifica slot</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Creazione/modifica attività -->
    <ModaleAttivita
      v-model="showModaleAttivita"
      :attivita="editingAttivita"
      :reminder="reminderInPresaInCarico"
      :all-attivita="[]"
      :departments="departments || []"
      :can-manage-activities="canManageActivities"
      :initial-start="newActivityStart"
      @saved="onModaleAttivitaSaved"
      @close="onModaleAttivitaClose"
    />
  </div>
</template>

<script>
import { Link } from '@inertiajs/vue3';
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import FullCalendar from '@fullcalendar/vue3';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin, { Draggable } from '@fullcalendar/interaction';
import itLocale from '@fullcalendar/core/locales/it';
import Loader from '@/Components/Loader.vue';
import ModaleAttivita from '@/Pages/Modules/attivita/Attivita/ModaleAttivita.vue';

const PRIORITY_COLORS = {
  bassa: '#22c55e',
  media: '#3b82f6',
  alta: '#f97316',
  critica: '#ef4444',
};

const STATUS_COMPLETED_COLOR = '#9ca3af';

const USER_COLOR_PALETTE = [
  '#3b82f6',
  '#8b5cf6',
  '#10b981',
  '#f97316',
  '#ef4444',
  '#06b6d4',
  '#ec4899',
  '#84cc16',
  '#6366f1',
  '#14b8a6',
  '#f59e0b',
  '#a855f7',
];

export default {
  props: ['users', 'isManager', 'departmentQueue', 'departments'],

  components: { Link, BreadCrumbs, FullCalendar, Loader, ModaleAttivita },

  data() {
    return {
      isLoading: false,
      isSaving: false,
      isDeletingActivity: false,
      filterPriority: '',
      filterStatus: '',
      filterUser: '',
      eventDrawer: {
        show: false,
        event: null,
        error: null,
      },
      runningTimer: null,
      timerLoading: false,
      timerDescription: '',
      splitDialog: {
        show: false,
        activityId: null,
        activityTitle: '',
        startDate: '',
        startTime: '08:30',
        durationHours: 1,
        allocationMode: 'segmented',
        error: null,
      },
      calendarConflictError: null,
      showModaleAttivita: false,
      editingAttivita: null,
      newActivityStart: null,
      localQueue: [],
      reminderInPresaInCarico: null,
      calendarFeed: null,
      calendarFeedModal: {
        show: false,
        loading: false,
        feedback: null,
      },
      navigation: [
        { name: 'Dashboard', href: route('dashboard') },
        { name: 'Attività', href: route('modules.home', 'attivita') },
        { name: 'Calendario', href: route('modules.attivita.calendario') },
      ],
      calendarOptions: {
        plugins: [dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin],
        initialView: 'timeGridWeek',
        locale: itLocale,
        nowIndicator: true, 
        scrollTime: '08:00:00',
        headerToolbar: {
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,timeGridDay,listMonth',
        },
        buttonText: {
          today: 'Oggi',
          month: 'Mese',
          week: 'Settimana',
          day: 'Giorno',
          list: 'Lista',
        },
        height: '100%',
        editable: true,
        droppable: true,
        selectable: true,
        dayMaxEvents: true,
        weekends: true,
        slotEventOverlap: true,
        eventMaxStack: 4,
        eventOverlap: (stillEvent, movingEvent) => !this.eventsShareOperationalUsers(stillEvent, movingEvent),
        selectOverlap: () => true,
        events: this.fetchEvents,
        dateClick: this.handleDateClick,
        eventClick: this.handleEventClick,
        eventDrop: this.handleEventDropOrResize,
        eventResize: this.handleEventDropOrResize,
        eventDidMount: this.applyEventStyle,
        eventReceive: this.handleReminderDrop,
        businessHours: false,
        
      },
    };
  },

  mounted() {
    this.localQueue = this.departmentQueue ? [...this.departmentQueue] : [];
    this.loadRunningTimer();

    this.$nextTick(() => {
      const container = document.getElementById('external-reminders-container');
      if (container) {
        new Draggable(container, {
          itemSelector: '.reminder-drag-item',
          eventData(eventEl) {
            return {
              title: eventEl.dataset.title,
              duration: '01:00',
              create: false,
              extendedProps: {
                reminder_id: eventEl.dataset.reminderId,
              },
            };
          },
        });
      }
    });
  },

  watch: {
    departmentQueue(val) {
      this.localQueue = val ? [...val] : [];
    },
  },

  computed: {
    userColorMap() {
      const map = {};
      (this.users || []).forEach((user, index) => {
        map[user.id] = USER_COLOR_PALETTE[index % USER_COLOR_PALETTE.length];
      });
      return map;
    },

    hasActiveFilters() {
      return !!(this.filterPriority || this.filterStatus || this.filterUser);
    },

    canManageActivities() {
      return this.$page.props.isAdminOrSuperAdmin || this.isManager;
    },

    currentCalendarEvent() {
      return this.eventDrawer.event;
    },

    currentEventProps() {
      return this.eventDrawer.event?.extendedProps || {};
    },

    isCurrentEventSlot() {
      return !!this.currentEventProps.is_slot;
    },

    canSavePlanning() {
      return !!(
        this.splitDialog.activityId &&
        this.splitDialog.startDate &&
        this.splitDialog.startTime &&
        Number(this.splitDialog.durationHours) > 0 &&
        this.splitDialog.allocationMode
      );
    },
  },

  methods: {
    async fetchEvents(fetchInfo, successCallback, failureCallback) {
      this.isLoading = true;
      try {
        const params = {
          start: fetchInfo.start.toISOString().split('T')[0],
          end: fetchInfo.end.toISOString().split('T')[0],
          priority: this.filterPriority,
          status: this.filterStatus,
          user: this.filterUser,
        };

        const response = await axios.get(route('modules.attivita.eventi-calendario'), { params });
        const canSeeAssignees = this.$page.props.isAdminOrSuperAdmin || this.isManager;
        const events = response.data.map(ev => {
          const isOperationallyUnassigned = canSeeAssignees && !(ev.extendedProps.operational_user_ids?.length > 0);

          let color;
          if (ev.extendedProps.status === 'completata') {
            color = STATUS_COMPLETED_COLOR;
          } else if (canSeeAssignees) {
            const firstOperationalUserId = ev.extendedProps.operational_user_ids?.[0];
            color = firstOperationalUserId
              ? (this.userColorMap[firstOperationalUserId] || '#6b7280')
              : '#6b7280';
          } else {
            color = PRIORITY_COLORS[ev.extendedProps.priority] || '#6b7280';
          }

          // Title logic is now kept only once
          let title = ev.title;
          if (canSeeAssignees) {
            const owners = ev.extendedProps.owners;
            title = owners ? `${ev.title} (${owners})` : `${ev.title} (non assegnata)`;
          }

          const mappedEvent = {
            ...ev,
            title,
            backgroundColor: color,
            borderColor: color,
            textColor: isOperationallyUnassigned ? '#111827' : '#ffffff',
          };

          if (ev.extendedProps.has_running_timer) {
            mappedEvent.backgroundColor = '#dcfce7';
            mappedEvent.borderColor = '#16a34a';
            mappedEvent.textColor = '#166534';
            mappedEvent.classNames = [...(mappedEvent.classNames || []), 'event-running-timer'];
          }

          return mappedEvent;
        });

        successCallback(events);
      } catch (e) {
        failureCallback(e);
      } finally {
        this.isLoading = false;
      }
    },

    async handleEventDropOrResize(info) {
      const event = info.event;
      const props = event.extendedProps;

      if (!event.start) {
        info.revert();
        return;
      }

      if (props.is_goods_receipt && props.is_non_blocking) {
        try {
          await axios.patch(route('modules.attivita.goods-estimated-arrival', props.activity_id), {
            goods_estimated_arrival_at: this.formatLocalDate(event.start),
          });
          this.refreshCalendar();
        } catch (e) {
          info.revert();
          this.calendarConflictError = e.response?.data?.message
            || e.response?.data?.error
            || 'Impossibile aggiornare la data stimata di arrivo merce.';
          setTimeout(() => { this.calendarConflictError = null; }, 6000);
        }
        return;
      }

      try {
        if (props.is_slot && props.slot_id) {
          await axios.patch(route('modules.attivita.aggiorna-slot', props.slot_id), {
            start_datetime: this.formatLocalDateTime(event.start),
            end_datetime: this.formatLocalDateTime(event.end),
          });

          this.refreshCalendar();
          return;
        }

        const startDate = this.formatLocalDate(event.start);

        let endDate = event.end ? new Date(event.end) : new Date(event.start);

        if (event.allDay && event.end) {
          endDate.setDate(endDate.getDate() - 1);
        }

        await axios.patch(route('modules.attivita.aggiorna-date', event.id), {
          start_date: startDate,
          end_date: this.formatLocalDate(endDate),
        });

        this.refreshCalendar();
      } catch (e) {
        console.error('Errore aggiornamento evento calendario:', e);
        info.revert();
        this.calendarConflictError = e.response?.data?.message
          || e.response?.data?.error
          || 'Impossibile aggiornare l\'evento nel calendario.';
        setTimeout(() => { this.calendarConflictError = null; }, 6000);
      }
    },

    getAssignedUserIds(event) {
      return event.extendedProps?.assigned_user_ids
        || event.extendedProps?.assigned_users?.map(u => u.id)
        || [];
    },

    getOperationalUserIds(event) {
      return event.extendedProps?.operational_user_ids
        || event.extendedProps?.assigned_users
            ?.filter(u => u.assignment_origin === 'direct')
            ?.map(u => u.id)
        || [];
    },

    eventsShareAssignedUsers(eventA, eventB) {
      const a = this.getAssignedUserIds(eventA);
      const b = this.getAssignedUserIds(eventB);
      if (!a.length || !b.length) return false;
      return a.some(id => b.includes(id));
    },

    eventsShareOperationalUsers(eventA, eventB) {
      const a = this.getOperationalUserIds(eventA);
      const b = this.getOperationalUserIds(eventB);
      if (!a.length || !b.length) return false;
      return a.some(id => b.includes(id));
    },

    formatLocalDate(date) {
      if (!date) return null;

      const pad = (value) => String(value).padStart(2, '0');

      return [
        date.getFullYear(),
        pad(date.getMonth() + 1),
        pad(date.getDate())
      ].join('-');
    },

    formatLocalDateTime(date) {
      if (!date) return null;

      const pad = (value) => String(value).padStart(2, '0');

      return `${this.formatLocalDate(date)} ${[
        pad(date.getHours()),
        pad(date.getMinutes()),
        pad(date.getSeconds())
      ].join(':')}`;
    },

    handleEventClick(info) {
      this.eventDrawer = {
        show: true,
        event: info.event,
        error: null,
      };
      this.timerDescription = '';
    },

    handleDateClick(info) {
      if (!this.canManageActivities) return;

      let start = info.date;
      if (info.allDay) {
        start = new Date(start);
        start.setHours(8, 0, 0, 0);
      }

      this.editingAttivita = null;
      this.newActivityStart = start;
      this.showModaleAttivita = true;
    },

    async editActivityFromDrawer() {
      const activityId = this.currentDrawerActivityId();
      if (!activityId) return;

      this.isLoading = true;
      try {
        const response = await axios.get(route('modules.attivita.dettaglio', activityId));
        this.editingAttivita = response.data;
        this.newActivityStart = null;
        this.closeEventDrawer();
        this.showModaleAttivita = true;
      } catch (error) {
        console.error('Errore nel caricamento dei dati dell\'attività:', error);
        const props = this.currentEventProps;
        this.editingAttivita = {
          id: activityId,
          title: this.eventDrawer.event?.title || '',
          description: props.description || '',
          company_name: props.company_name || null,
          priority: props.priority || 'media',
          status: props.status || 'nuova',
          activity_type: props.activity_type || 'standard',
          order_id: props.order_id || null,
          start_date: this.eventDrawer.event?.start ? this.toInputDate(this.eventDrawer.event.start) : null,
          end_date: this.eventDrawer.event?.end ? this.toInputDate(this.eventDrawer.event.end) : null,
        };
        this.newActivityStart = null;
        this.closeEventDrawer();
        this.showModaleAttivita = true;
      } finally {
        this.isLoading = false;
      }
    },

    async deleteActivityFromDrawer() {
      const activityId = this.currentDrawerActivityId();
      if (!activityId) return;

      const title = this.currentCalendarEvent?.title || 'questa attività';
      const confirmMessage = (this.$t ? this.$t('at.lista_attivita.delete_confirm_text') : "Sei sicuro di voler procedere con l'eliminazione dell'attività?")
        + '\n\n' + title;

      if (!confirm(confirmMessage)) {
        return;
      }

      this.isDeletingActivity = true;
      try {
        await axios.delete(route('modules.attivita.elimina-attivita', activityId));
        this.closeEventDrawer();
        this.refreshCalendar();
      } catch (e) {
        console.error('Errore eliminazione attività:', e);
        this.eventDrawer.error = e.response?.data?.message
          || e.response?.data?.error
          || 'Errore durante l\'eliminazione dell\'attività.';
      } finally {
        this.isDeletingActivity = false;
      }
    },

    onModaleAttivitaSaved(payload) {
      this.showModaleAttivita = false;
      this.editingAttivita = null;
      this.newActivityStart = null;

      if (payload?.isReminder) {
        this.localQueue = this.localQueue.filter(r => r.id !== payload.reminderId);
        this.loadRunningTimer();
      }
      this.reminderInPresaInCarico = null;

      this.refreshCalendar();
    },

    onModaleAttivitaClose() {
      this.editingAttivita = null;
      this.newActivityStart = null;
      this.reminderInPresaInCarico = null;
    },

    closeEventDrawer() {
      this.eventDrawer = {
        show: false,
        event: null,
        error: null,
      };
      this.timerDescription = '';
    },

    async markGoodsReceived() {
      const activityId = this.currentEventProps?.activity_id;
      if (!activityId) return;

      this.timerLoading = true;
      this.eventDrawer.error = null;

      try {
        await axios.post(route('modules.attivita.goods-received', activityId));
        this.closeEventDrawer();
        this.refreshCalendar();
      } catch (e) {
        this.eventDrawer.error = e.response?.data?.message || 'Errore durante la conferma ricezione merce.';
      } finally {
        this.timerLoading = false;
      }
    },

    applyEventStyle(info) {
      const props   = info.event.extendedProps;
      const bgColor = info.event.backgroundColor;
      const isTimeGrid     = info.view.type.startsWith('timeGrid');
      const isCompleted    = props.status === 'completata';
      const canSeeAssignees = this.$page.props.isAdminOrSuperAdmin || this.isManager;
      const isUnassigned   = canSeeAssignees && !(props.operational_user_ids?.length > 0);

      const el = info.el;

      if (isTimeGrid) {
        el.style.borderRadius = '6px';
        el.style.boxShadow    = '0 1px 3px rgba(0,0,0,0.08)';
      }

      if (isCompleted) {
        if (isTimeGrid) {
          el.style.background  = `repeating-linear-gradient(-45deg, ${bgColor}28, ${bgColor}28 5px, ${bgColor}50 5px, ${bgColor}50 11px)`;
          el.style.borderLeft  = `4px solid ${bgColor}`;
          el.style.color       = '#6b7280';
          el.style.borderTop   = 'none';
          el.style.borderRight = 'none';
          el.style.borderBottom = 'none';
        } else {
          el.style.background = `repeating-linear-gradient(-45deg, ${bgColor}, ${bgColor} 5px, ${bgColor}bb 5px, ${bgColor}bb 11px)`;
          el.style.opacity    = '0.75';
        }
      } else if (isUnassigned) {
        if (isTimeGrid) {
          el.style.background = 'repeating-linear-gradient(45deg, #fed7aa 0, #fed7aa 8px, #fb923c 8px, #fb923c 16px)';
          el.style.border     = '2px dashed #ea580c';
          el.style.color      = '#111827';
          el.style.opacity    = '1';
          el.style.fontWeight = '600';
          el.style.setProperty('--fc-event-text-color', '#111827');
          el.querySelectorAll('.fc-event-main, .fc-event-title, .fc-event-time').forEach(child => {
            child.style.color = '#111827';
          });
        } else {
          el.style.background = 'repeating-linear-gradient(90deg, #fb923c 0, #fb923c 5px, #fed7aa 5px, #fed7aa 10px)';
          el.style.border     = '1px solid #ea580c';
          el.style.color      = '#111827';
          el.style.opacity    = '1';
          el.style.fontWeight = '600';
        }
      } else if (isTimeGrid) {
        el.style.borderLeft  = `4px solid ${bgColor}`;
        el.style.background  = `${bgColor}`;
        el.style.color       = '#111827';
        el.style.borderTop   = 'none';
        el.style.borderRight = 'none';
        el.style.borderBottom = 'none';
      }

      if (props.priority === 'critica' && !isCompleted) {
        el.style.fontWeight = '600';
      }
    },

    refreshCalendar() {
      this.$refs.fullCalendar?.getApi().refetchEvents();
    },

    resetFilters() {
      this.filterPriority = '';
      this.filterStatus = '';
      this.filterUser = '';
      this.refreshCalendar();
    },

    openPlanningDialog() {
      const ev = this.eventDrawer.event;
      if (!ev) return;

      const startDate = ev.start ? this.toInputDate(ev.start) : this.toInputDate(new Date());
      const defaultHours = Number(ev.extendedProps.total_hours || ev.extendedProps.duration_hours || 1);

      this.closeEventDrawer();

      this.splitDialog = {
        show: true,
        activityId: ev.extendedProps.activity_id || ev.id,
        activityTitle: ev.title,
        startDate,
        startTime: '08:30',
        durationHours: defaultHours > 0 ? defaultHours : 1,
        allocationMode: 'segmented',
        error: null,
      };
    },

    // Alias mantenuto per compatibilità con eventuali riferimenti esistenti.
    openSplitDialog() {
      this.openPlanningDialog();
    },

    async saveSplit() {
      if (!this.canSavePlanning) return;

      this.isSaving = true;
      this.splitDialog.error = null;

      try {
        await axios.post(
          route('modules.attivita.alloca-slot-attivita', this.splitDialog.activityId),
          {
            scheduling_mode: 'duration',
            allocation_mode: this.splitDialog.allocationMode,
            start_date: this.splitDialog.startDate,
            start_datetime: this.splitDialog.startTime,
            duration_hours: this.splitDialog.durationHours,
          }
        );

        this.splitDialog.show = false;
        this.refreshCalendar();
      } catch (e) {
        this.splitDialog.error =
          e.response?.data?.message ||
          e.response?.data?.error ||
          'Errore durante la pianificazione degli slot.';
      } finally {
        this.isSaving = false;
      }
    },

    async removeAllSlots() {
      const activityId = this.currentEventProps.activity_id;
      this.closeEventDrawer();

      try {
        await axios.delete(route('modules.attivita.elimina-slots', activityId));
        this.refreshCalendar();
      } catch (e) {
        console.error('Errore nella rimozione degli slot:', e);
      }
    },

    eventDateLabel(event) {
      if (!event) return '—';

      if (event.extendedProps.is_slot) {
        return `${this.formatDatetime(event.start)} → ${this.formatDatetime(event.end)}`;
      }

      if (event.start && event.end) {
        return `${this.formatDate(event.start)} → ${this.formatDate(event.end)}`;
      }

      if (event.start) {
        return this.formatDate(event.start);
      }

      return '—';
    },

    formatDatetime(date) {
      if (!date) return '—';
      const d = date instanceof Date ? date : new Date(date);
      return d.toLocaleString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },

    formatDate(date) {
      if (!date) return '—';
      const d = date instanceof Date ? date : new Date(date);
      return d.toLocaleDateString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    },

    toInputDate(date) {
      const d = date instanceof Date ? date : new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    allocationModeCardClass(mode) {
      const base = 'w-full flex items-start gap-3 p-4 rounded-xl border text-left transition-all';
      const isActive = this.splitDialog.allocationMode === mode;

      if (mode === 'segmented') {
        return `${base} ${isActive
          ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200 shadow-sm'
          : 'border-blue-100 bg-white hover:bg-blue-50'}`;
      }

      return `${base} ${isActive
        ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200 shadow-sm'
        : 'border-indigo-100 bg-white hover:bg-indigo-50'}`;
    },

    async loadRunningTimer() {
      try {
        const res = await axios.get(route('modules.attivita.timer.current'));
        this.runningTimer = res.data.timer || null;
      } catch {
        this.runningTimer = null;
      }
    },

    currentDrawerActivityId() {
      const ev = this.eventDrawer.event;
      if (!ev) return null;
      return ev.extendedProps?.activity_id || ev.id;
    },

    isTimerRunningForCurrentActivity() {
      const id = this.currentDrawerActivityId();
      return !!(this.runningTimer && this.runningTimer.activity_id === id);
    },

    hasRunningTimerOnOtherActivity() {
      const id = this.currentDrawerActivityId();
      return !!(this.runningTimer && this.runningTimer.activity_id !== id);
    },

    async startTimerFromDrawer() {
      const activityId = this.currentDrawerActivityId();
      if (!activityId) return;

      this.timerLoading = true;
      this.eventDrawer.error = null;

      try {
        const res = await axios.post(route('modules.attivita.timer.start', activityId));
        this.runningTimer = res.data.timer;
        this.refreshCalendar();
      } catch (e) {
        this.eventDrawer.error =
          e.response?.data?.message ||
          'Errore durante l\'avvio della lavorazione.';
      } finally {
        this.timerLoading = false;
      }
    },

    async stopTimerFromDrawer() {
      const activityId = this.currentDrawerActivityId();
      if (!activityId) return;

      this.timerLoading = true;
      this.eventDrawer.error = null;

      try {
        await axios.post(route('modules.attivita.timer.stop', activityId), {
          description: this.timerDescription || '',
        });
        this.runningTimer = null;
        this.timerDescription = '';
        this.refreshCalendar();
      } catch (e) {
        this.eventDrawer.error =
          e.response?.data?.message ||
          'Errore durante lo stop della lavorazione.';
      } finally {
        this.timerLoading = false;
      }
    },

    async stopAndCompleteFromDrawer() {
      const activityId = this.currentDrawerActivityId();
      if (!activityId) return;

      this.timerLoading = true;
      this.eventDrawer.error = null;

      try {
        await axios.post(route('modules.attivita.timer.stop-complete', activityId), {
          description: this.timerDescription || '',
        });
        this.runningTimer = null;
        this.timerDescription = '';
        this.closeEventDrawer();
        this.refreshCalendar();
      } catch (e) {
        this.eventDrawer.error =
          e.response?.data?.message ||
          'Errore durante la chiusura dell\'attività.';
      } finally {
        this.timerLoading = false;
      }
    },

    formatTimerTime(isoString) {
      if (!isoString) return '—';
      const d = new Date(isoString);
      return d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    },

    priorityLabel(priority) {
      return {
        bassa: 'Bassa',
        media: 'Media',
        alta: 'Alta',
        critica: 'Critica',
      }[priority] || priority || '—';
    },

    statusLabel(status) {
      return {
        nuova: 'Nuova',
        assegnata: 'Assegnata',
        schedulata: 'Schedulata',
        in_lavorazione: 'In lavorazione',
        completata: 'Completata',
      }[status] || status || '—';
    },

    priorityBadgeClass(priority) {
      return {
        bassa: 'bg-green-500',
        media: 'bg-blue-500',
        alta: 'bg-orange-500',
        critica: 'bg-red-500',
      }[priority] || 'bg-gray-400';
    },

    prioritySoftBadgeClass(priority) {
      return {
        bassa: 'bg-green-100 text-green-700',
        media: 'bg-blue-100 text-blue-700',
        alta: 'bg-orange-100 text-orange-700',
        critica: 'bg-red-100 text-red-700',
      }[priority] || 'bg-gray-100 text-gray-700';
    },

    statusBadgeClass(status) {
      return {
        nuova: 'bg-gray-100 text-gray-700',
        assegnata: 'bg-blue-100 text-blue-700',
        schedulata: 'bg-purple-100 text-purple-700',
        in_lavorazione: 'bg-orange-100 text-orange-700',
        completata: 'bg-green-100 text-green-700',
      }[status] || 'bg-gray-100 text-gray-700';
    },

    async openCalendarFeedModal() {
      this.calendarFeedModal.show    = true;
      this.calendarFeedModal.loading = true;
      this.calendarFeedModal.feedback = null;

      try {
        const res = await axios.get(route('modules.attivita.calendar.feed'));
        this.calendarFeed = res.data;
      } catch {
        this.calendarFeedModal.feedback = {
          type: 'error',
          message: 'Errore nel caricamento del feed calendario.',
        };
      } finally {
        this.calendarFeedModal.loading = false;
      }
    },

    async copyCalendarFeedUrl() {
      const url = this.calendarFeed?.url;
      if (! url) return;

      try {
        await navigator.clipboard.writeText(url);
        this.calendarFeedModal.feedback = { type: 'success', message: 'Link copiato.' };
      } catch {
        window.prompt('Copia questo link:', url);
      }
    },

    async regenerateCalendarFeed() {
      try {
        const res = await axios.post(route('modules.attivita.calendar.feed.regenerate'));
        this.calendarFeed = res.data;
        this.calendarFeedModal.feedback = { type: 'success', message: 'Nuovo link generato.' };
      } catch {
        this.calendarFeedModal.feedback = {
          type: 'error',
          message: 'Errore durante la rigenerazione del link.',
        };
      }
    },

    async disableCalendarFeed() {
      if (! confirm('Disattivare il link calendario? Google Calendar non riuscirà più ad aggiornare questo feed.')) {
        return;
      }

      try {
        await axios.post(route('modules.attivita.calendar.feed.disable'));
        this.calendarFeed.active = false;
        this.calendarFeedModal.feedback = { type: 'success', message: 'Link calendario disattivato.' };
      } catch {
        this.calendarFeedModal.feedback = {
          type: 'error',
          message: 'Errore durante la disattivazione del link.',
        };
      }
    },

    handleReminderDrop(info) {
      const reminderId = info.event.extendedProps?.reminder_id;
      const start       = info.event.start;
      info.event.remove();

      if (!reminderId) return;

      const reminder = this.localQueue.find(r => String(r.id) === String(reminderId));
      if (!reminder) return;

      this.apriPresaInCaricoModal(reminder, start);
    },

    apriPresaInCaricoModal(reminder, initialStart = null) {
      this.editingAttivita = null;
      this.reminderInPresaInCarico = reminder;
      this.newActivityStart = initialStart || new Date();
      this.showModaleAttivita = true;
    },

    async loadDepartmentQueue() {
      try {
        const res = await axios.get(route('modules.attivita.calendario'), {
          headers: { 'X-Inertia': 'true', 'X-Inertia-Partial-Component': 'Modules/attivita/CalendarioAttivita', 'X-Inertia-Partial-Data': 'departmentQueue' },
        });
        if (res.data?.props?.departmentQueue) {
          this.localQueue = res.data.props.departmentQueue;
        }
      } catch {
        /* silently ignore */
      }
    },
  },
};
</script>

<style scoped>
.calendar-select,
.form-input-soft {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  color: #374151;
  background-color: #ffffff;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
}

.calendar-select:focus,
.form-input-soft:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.25);
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  white-space: nowrap;
}

.legend-dot {
  display: inline-block;
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 0.125rem;
}

.info-card {
  border: 1px solid #e5e7eb;
  background-color: #ffffff;
  border-radius: 0.75rem;
  padding: 0.75rem;
}

.info-label {
  display: block;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6b7280;
  margin-bottom: 0.35rem;
}

.info-value {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #111827;
  word-break: break-word;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.18s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.action-panel-fade-enter-active,
.action-panel-fade-leave-active {
  transition: opacity 0.18s ease;
}

.action-panel-fade-enter-from,
.action-panel-fade-leave-to {
  opacity: 0;
}

.action-panel-slide-enter-active,
.action-panel-slide-leave-active {
  transition: transform 0.22s ease;
}

.action-panel-slide-enter-from,
.action-panel-slide-leave-to {
  transform: translateX(100%);
}

.action-panel-slide-enter-to,
.action-panel-slide-leave-from {
  transform: translateX(0);
}

:deep(.fc) {
  font-size: 0.875rem;
}

:deep(.fc .fc-toolbar-title) {
  font-size: 1.15rem;
  font-weight: 700;
  color: #111827;
}

:deep(.fc .fc-button) {
  border-radius: 0.5rem;
  border-color: #d1d5db;
  background-color: #ffffff;
  color: #374151;
  box-shadow: none;
  text-transform: none;
  font-weight: 500;
  padding: 0.4rem 0.7rem;
}

:deep(.fc .fc-button:hover),
:deep(.fc .fc-button:focus) {
  background-color: #f9fafb;
  border-color: #9ca3af;
  color: #111827;
}

:deep(.fc .fc-button-primary:not(:disabled).fc-button-active) {
  background-color: #2563eb;
  border-color: #2563eb;
  color: #ffffff;
}

:deep(.fc .fc-event) {
  border-radius: 0.45rem;
  padding: 1px 3px;
  font-weight: 500;
}

:deep(.fc .fc-daygrid-day-number) {
  color: #374151;
  font-weight: 500;
}

:deep(.fc .fc-col-header-cell-cushion) {
  color: #4b5563;
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.04em;
}

:deep(.fc-event.event-running-timer) {
  border-width: 2px;
  font-weight: 700;
  box-shadow: 0 0 0 2px rgba(22, 163, 74, 0.18);
}

:deep(.fc-event.event-running-timer .fc-event-title::before) {
  content: '▶ ';
}

:deep(.fc-event.event-goods-receipt-waiting) {
  background-color: #fef3c7 !important;
  border: 2px dashed #d97706 !important;
  color: #92400e !important;
}
</style>
